import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../api";
import { apiUrl } from "../auth";
import { ArrowLeft, Loader2, FileText } from "lucide-react";
import {
  TIMESTAMP_FIELDS,
  MEDICAL_HISTORY,
  ASSESSMENT,
  VITAL_COLUMNS,
  BODY_REGIONS,
  INTERVENTION_AIRWAY,
  INTERVENTION_BREATHING,
  INTERVENTION_TRAUMA,
  MED_COLUMNS,
  ageFromDob,
} from "@ambulance/shared";
import { BurnDiagram } from "../components/BurnDiagram";
import { SignatureView } from "../components/SignaturePad";
import { OfficialPcrForm } from "@ambulance/shared";

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden break-inside-avoid">
      <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-800">
        {title}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function KV({ items }: { items: [string, string][] }) {
  const filled = items.filter(([, v]) => v && String(v).trim());
  if (!filled.length) return <p className="text-sm text-slate-400">—</p>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2 text-sm">
      {filled.map(([k, v]) => (
        <div key={k}>
          <span className="text-xs uppercase tracking-wide text-slate-400 block">
            {k}
          </span>
          <span className="text-slate-800">{v}</span>
        </div>
      ))}
    </div>
  );
}

function Chips({
  items,
  values,
  other,
}: {
  items: ReadonlyArray<readonly [string, string]>;
  values: Record<string, boolean>;
  other?: string;
}) {
  const on = items.filter(([k]) => values?.[k]);
  if (!on.length && !other?.trim())
    return <span className="text-sm text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5 text-sm">
      {on.map(([k, l]) => (
        <span
          key={k}
          className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700"
        >
          {l}
        </span>
      ))}
      {other?.trim() && (
        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
          {other}
        </span>
      )}
    </div>
  );
}

export function PcrReportDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const api = useApi();
  const q = useQuery({ queryKey: ["pcr-report", id, api.token], queryFn: () => api.get<Record<string, unknown>>(`/api/v1/pcr-reports/${id}`), enabled: Boolean(api.token) && Boolean(id) });
  const data = q.isPending ? undefined : q.isError ? null : q.data;
  const [pdfBusy, setPdfBusy] = useState(false);

  if (data === undefined) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading PCR…
      </div>
    );
  }
  if (data === null) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-700 mb-4">PCR not found.</p>
        <button
          onClick={() => navigate({ to: "/pcr-reports" })}
          className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300"
        >
          Back to reports
        </button>
      </div>
    );
  }

  const d = data as any;
  const p = d.patient ?? {};
  const ca = d.cardiacArrest ?? {};
  const ref = d.refusal ?? {};
  const ho = d.handover ?? {};
  // New records store an array of staff; older ones kept a single staff on the
  // handover record itself.
  const staff: Record<string, string>[] =
    d.handoverStaff && d.handoverStaff.length
      ? d.handoverStaff
      : ho.staffName || ho.signature || ho.position || ho.idNumber
        ? [
            {
              staffName: ho.staffName,
              position: ho.position,
              idNumber: ho.idNumber,
              signature: ho.signature,
            },
          ]
        : [];
  const cannula = d.cannula ?? {};
  // New records store arrays; older ones a single cannula / receiver, and a
  // single firstShockTime instead of a shocks array.
  const cannulas: Record<string, string>[] =
    Array.isArray(d.cannulas) && d.cannulas.length
      ? d.cannulas
      : cannula.site || cannula.gauge || cannula.attempts || cannula.inserted
        ? [cannula]
        : [];
  const receivers: Record<string, string>[] =
    Array.isArray(d.handoverReceivers) && d.handoverReceivers.length
      ? d.handoverReceivers
      : ho.receivedBy || ho.receivedPosition || ho.receivedSignature
        ? [
            {
              name: ho.receivedBy,
              position: ho.receivedPosition,
              signature: ho.receivedSignature,
            },
          ]
        : [];
  const shockTimes: string = (
    Array.isArray(d.cardiacShocks) && d.cardiacShocks.length
      ? d.cardiacShocks
      : ca.firstShockTime
        ? [ca.firstShockTime]
        : []
  )
    .filter((t: string) => t && String(t).trim())
    .join(", ");
  const gcsTotal =
    (Number(d.gcs?.eye) || 0) +
    (Number(d.gcs?.verbal) || 0) +
    (Number(d.gcs?.motor) || 0);

  // Print only the official-form layout (portrait A4). Injects a print-time
  // stylesheet that overrides the app's default landscape @page and isolates
  // #official-pcr, then removes it after printing. Everything else is also
  // removed from layout (display:none via :has) so the hidden report can't
  // produce trailing blank pages.
  function printOfficial() {
    const style = document.createElement("style");
    style.textContent = [
      "@media print{",
      "@page{size:A4 portrait;margin:5mm}",
      "html,body{height:auto!important}",
      "body *{visibility:hidden!important}",
      "#official-pcr,#official-pcr *{visibility:visible!important}",
      "#official-pcr{display:block!important;position:absolute;left:0;top:0;width:100%}",
      "body *:not(:has(#official-pcr)):not(#official-pcr):not(#official-pcr *){display:none!important}",
      "}",
    ].join("");
    document.head.appendChild(style);
    const cleanup = () => {
      style.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
  }

  // Generate the official-form PDF on the server so it's identical on every
  // device (matches the desktop layout). Falls back to the browser print dialog
  // on failure. Same pipeline as the ambulance checklist.
  async function downloadPdf() {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/v1/pcr-reports/${id}/pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${api.token}` },
      });
      if (!res.ok) throw new Error("PDF request failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        `PCR ${d.caseNumber ?? ""} ${d.date ?? ""}`.trim() + ".pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      printOfficial();
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="force-print-colors doc-print max-w-5xl mx-auto">
      <div className="no-print flex items-center justify-between mb-4 gap-2">
        <button
          onClick={() => navigate({ to: "/pcr-reports" })}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={downloadPdf}
          disabled={pdfBusy}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-[var(--hmg-red)] text-white hover:bg-[var(--hmg-red-dark)] text-sm font-semibold disabled:opacity-60"
        >
          {pdfBusy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">
            {pdfBusy ? "Preparing PDF…" : "Download PDF"}
          </span>
          <span className="sm:hidden">{pdfBusy ? "…" : "PDF"}</span>
        </button>
      </div>

      <div id="official-pcr">
        <OfficialPcrForm d={d} />
      </div>

      <div className="flex items-center gap-3 sm:gap-4 border-b-2 border-[var(--hmg-red)] pb-3 mb-4">
        <img
          src="/hmg-logo.png"
          alt="Dr Sulaiman Al Habib Medical Group"
          width={240}
          height={62}
          className="h-11 sm:h-14 w-auto flex-shrink-0"
        />
        <div className="border-l border-slate-300 pl-3 sm:pl-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[var(--hmg-red)] font-bold">
            Emergency Medical Services
          </div>
          <div className="text-base sm:text-xl font-bold text-slate-900">
            Patient Care Report
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <Card title="Call & Timestamps">
          <KV
            items={[
              ["Case Number", d.caseNumber],
              ["Date", d.date],
              ["Center", d.hospitalName],
              ["Unit Code", d.unitCode],
              ["Destination", d.destination],
              ["Submitted", new Date(d.submittedAt).toLocaleString(undefined, { hour12: false })],
              ...TIMESTAMP_FIELDS.map(
                ([k, l]) => [l, d.timestamps?.[k] ?? ""] as [string, string]
              ),
            ]}
          />
        </Card>

        <Card title="Patient Information">
          <KV
            items={[
              [
                "Name",
                [p.firstName, p.secondName, p.lastName].filter(Boolean).join(" "),
              ],
              ["Date of Birth", p.dob],
              ["Age", p.dob ? ageFromDob(p.dob) : p.age],
              ["Gender", p.gender],
              ["Nationality", p.nationality],
              ["Mobile", p.mobile],
              ["Address", p.address],
              ["ID Type", p.idType],
              ["Patient ID", p.patientId],
              ["MRN", p.mrn],
            ]}
          />
        </Card>

        <Card title="Triage & Chief Complaint">
          <KV
            items={[
              ["Triage Level", d.triageLevel],
              ["Chief Complaint", d.chiefComplaint],
            ]}
          />
        </Card>

        <Card title="Allergies & Medical History">
          <div className="space-y-2">
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                Allergies
              </span>
              <Chips
                items={[
                  ["none", "NONE"],
                  ["unknown", "Unknown"],
                ]}
                values={d.allergies ?? {}}
                other={d.allergiesOther}
              />
            </div>
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                Medical History
              </span>
              <Chips
                items={MEDICAL_HISTORY}
                values={d.medicalHistory ?? {}}
                other={d.medicalHistoryOther}
              />
            </div>
          </div>
        </Card>

        <Card title="Patient Assessment">
          <KV
            items={ASSESSMENT.map(
              (a) => [a.label, d.assessment?.[a.key] ?? ""] as [string, string]
            )}
          />
        </Card>

        <Card title="Glasgow Coma Scale">
          <KV
            items={[
              ["Eye", d.gcs?.eye],
              ["Verbal", d.gcs?.verbal],
              ["Motor", d.gcs?.motor],
              ["Total", `${gcsTotal} / 15`],
            ]}
          />
        </Card>

        {Array.isArray(d.vitals) && d.vitals.some((r: any) =>
          Object.values(r).some((v) => v && String(v).trim())
        ) && (
          <Card title="Vital Signs">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    {VITAL_COLUMNS.map(([k, l]) => (
                      <th key={k} className="px-2 py-1">
                        {l}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {d.vitals.map((row: any, i: number) => (
                    <tr key={i}>
                      {VITAL_COLUMNS.map(([k]) => (
                        <td key={k} className="px-2 py-1 text-slate-700">
                          {row[k] || "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {d.cardiacArrestOccurred && (
          <Card title="Cardiac Arrest">
            <KV
              items={[
                ["First Rhythm", ca.firstRhythm],
                ["Time of Start", ca.timeOfStart],
                ["Bystander CPR", ca.bystanderCpr],
                ["Cycle", ca.cycle],
                ["Total CPR Time", ca.totalCprTime],
                ["AED", ca.aed],
                ["Defibrillator", ca.defibrillator],
                ["Shock Delivered", ca.shockDelivered],
                ["Shock Times", shockTimes],
                ["ROSC", ca.rosc],
                ["Time of ROSC", ca.timeOfRosc],
                ["Post ROSC Care", ca.postRoscCare],
                ["Dead", ca.dead],
                ["Death Time", ca.deathTime],
                ["Signs of Obvious Death", ca.signsOfObviousDeath],
                ["Dispatch Coordination", ca.dispatchCoordination],
                ["Body Disposition", ca.bodyDisposition],
                ["Note", ca.note],
              ]}
            />
          </Card>
        )}

        {d.presentingHistory?.trim() && (
          <Card title="Presenting Medical History">
            <p className="text-sm text-slate-700 whitespace-pre-wrap">
              {d.presentingHistory}
            </p>
          </Card>
        )}

        <Card title="Physical Examination">
          <KV
            items={[
              ...BODY_REGIONS.map(
                (r) => [r, d.physicalExam?.[r] ?? ""] as [string, string]
              ),
              ["Note", d.physicalExamNote],
            ]}
          />
          {(() => {
            const burnAreas = (d.burnAreas ?? {}) as Record<string, boolean>;
            const hasBurns =
              Object.values(burnAreas).some(Boolean) ||
              (d.burnTbsa && String(d.burnTbsa).trim());
            return hasBurns ? (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                  Burns
                </div>
                <BurnDiagram
                  value={burnAreas}
                  mode={d.burnAgeMode === "pediatric" ? "pediatric" : "adult"}
                  tbsa={d.burnTbsa ?? ""}
                  readOnly
                />
              </div>
            ) : null;
          })()}
        </Card>

        <Card title="Intervention">
          <div className="space-y-2">
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                Airway
              </span>
              <Chips items={INTERVENTION_AIRWAY} values={d.interventionAirway ?? {}} />
            </div>
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                Breathing
              </span>
              <Chips
                items={INTERVENTION_BREATHING}
                values={d.interventionBreathing ?? {}}
              />
            </div>
            <div>
              <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                Trauma
              </span>
              <Chips items={INTERVENTION_TRAUMA} values={d.interventionTrauma ?? {}} />
            </div>
            {cannulas.length > 0 && (
              <div>
                <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                  IV Cannula
                </span>
                <div className="space-y-0.5">
                  {cannulas.map((c, i) => (
                    <div key={i} className="text-sm text-slate-700">
                      #{i + 1}: Site {c.site || "—"} · Gauge {c.gauge || "—"} ·
                      Attempts {c.attempts || "—"}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <KV items={[["Note", d.interventionNote]]} />
            {Array.isArray(d.medications) && d.medications.length > 0 && (
              <div className="overflow-x-auto pt-1">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      {MED_COLUMNS.map(([k, l]) => (
                        <th key={k} className="px-2 py-1">
                          {l}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.medications.map((row: any, i: number) => (
                      <tr key={i}>
                        {MED_COLUMNS.map(([k]) => (
                          <td key={k} className="px-2 py-1 text-slate-700">
                            {row[k] || "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>

        {d.refusalOfTransport && (
          <Card title="Refusal of Transport">
            <KV
              items={[
                ["Name", ref.name],
                ["ID", ref.id],
                ["Phone", ref.phone],
                ["Date", ref.date],
                ["Witness / Relative", ref.witnessName],
              ]}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              <div>
                <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                  Patient Signature
                </span>
                <SignatureView value={ref.signature} />
              </div>
              <div>
                <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                  Witness Signature
                </span>
                <SignatureView value={ref.witnessSignature} />
              </div>
            </div>
          </Card>
        )}

        <Card title="Patient Handover">
          <KV items={[["Team Type", ho.teamType]]} />
          {staff.length > 0 && (
            <div className="mt-3 space-y-3">
              {staff.map((s, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-slate-200 p-3 break-inside-avoid"
                >
                  <div className="text-xs font-semibold text-slate-500 mb-1">
                    Staff {i + 1}
                  </div>
                  <KV
                    items={[
                      ["Staff Name", s.staffName],
                      ["Position", s.position],
                      ["ID Number", s.idNumber],
                    ]}
                  />
                  <div className="mt-2">
                    <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                      Signature
                    </span>
                    <SignatureView value={s.signature} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {receivers.length > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-3">
              <div className="text-xs font-semibold text-slate-500 mb-1">
                Received By (Receiving Facility)
              </div>
              <div className="space-y-3">
                {receivers.map((r, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-slate-200 p-3 break-inside-avoid"
                  >
                    <div className="text-xs font-semibold text-slate-500 mb-1">
                      Receiver {i + 1}
                    </div>
                    <KV
                      items={[
                        ["Received By", r.name],
                        ["Receiver Position", r.position],
                      ]}
                    />
                    <div className="mt-2">
                      <span className="text-xs uppercase tracking-wide text-slate-400 block mb-1">
                        Receiver Signature
                      </span>
                      <SignatureView value={r.signature} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
